import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  RefreshCw, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Users, 
  User,
  Settings,
  CheckCircle,
  X,
  Mail,
  Tag,
  ArrowUpDown,
  Filter,
  Check,
  Save,
  ExternalLink,
  UserIcon,
  Zap,
  Loader2
} from 'lucide-react';
import { SenderRule } from '../types';
import { formatDate, getRuleTypeText } from '../utils';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { getApiBaseUrl } from '../config/env';
import { batchUpdateUserIds, lookupUserByEmail } from '../services/api';
import toast from 'react-hot-toast';

const SenderRulesPage: React.FC = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState<SenderRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState<SenderRule | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [showBatchResults, setShowBatchResults] = useState(false);
  const [batchResults, setBatchResults] = useState<{
    success: number;
    failed: number;
    results: Array<{ id: number; email: string; success: boolean; user_id?: string; error?: string }>;
  } | null>(null);


  // 跳转到聊天室的函数
  const handleNavigateToChat = (senderEmail: string) => {
    navigate(`/chat?sender=${encodeURIComponent(senderEmail)}`);
  };
  const fetchRules = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('rule_type', filterType);
      if (filterActive !== 'all') params.append('active_only', filterActive === 'active' ? 'true' : 'false');
      if (searchTerm) params.append('search', searchTerm);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      const response = await fetch(`${getApiBaseUrl()}/sender-rules?${params}`);
      const data = await response.json();
      setRules(data.rules || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('获取用户规则失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (rule: SenderRule) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/sender-rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rule, is_active: !rule.is_active })
      });
      
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      console.error('切换规则状态失败:', error);
    }
  };

  const handleDeleteRule = async (rule: SenderRule) => {
    if (!window.confirm(`确定要删除用户 ${rule.sender_email} 的规则吗？`)) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/sender-rules/${rule.id}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      console.error('删除规则失败:', error);
    }
  };

  const handleUpdateTags = async (rule: SenderRule, newTags: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/sender-rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: newTags })
      });
      
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      console.error('更新标签失败:', error);
    }
  };

  const handleUpdateUserId = async (rule: SenderRule, newUserId: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/sender-rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: newUserId })
      });
      
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      console.error('更新用户ID失败:', error);
    }
  };

  const handleUpdateRuleType = async (rule: SenderRule, ruleType: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/sender-rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rule_type: ruleType })
      });
      
      if (response.ok) {
        fetchRules();
      }
    } catch (error) {
      console.error('更新规则类型失败:', error);
    }
  };

  const handleBatchUpdateUserIds = async () => {
    if (!window.confirm('确定要为所有缺少用户ID的规则自动配置用户ID吗？')) return;
    
    setBatchUpdating(true);
    setShowBatchResults(false);
    
    try {
      // 筛选出没有用户ID的规则
      const rulesWithoutUserId = rules.filter(rule => !rule.user_id);
      
      if (rulesWithoutUserId.length === 0) {
        toast.success('所有规则都已配置用户ID');
        setBatchUpdating(false);
        return;
      }
      
      toast.loading(`正在为 ${rulesWithoutUserId.length} 个规则配置用户ID...`);
      
      const rulesToUpdate = rulesWithoutUserId.map(rule => ({
        id: rule.id,
        email: rule.sender_email
      }));
      
      const results = await batchUpdateUserIds(rulesToUpdate);
      
      setBatchResults(results);
      setShowBatchResults(true);
      
      toast.dismiss();
      
      if (results.success > 0) {
        toast.success(`成功配置 ${results.success} 个用户ID`);
        fetchRules(); // 刷新列表
      }
      
      if (results.failed > 0) {
        toast.error(`${results.failed} 个规则配置失败`);
      }
      
    } catch (error) {
      console.error('批量配置用户ID失败:', error);
      toast.dismiss();
      toast.error('批量配置用户ID失败');
    } finally {
      setBatchUpdating(false);
    }
  };

  const handleLookupSingleUser = async (rule: SenderRule) => {
    try {
      const result = await lookupUserByEmail(rule.sender_email);
      
      if (result.found && result.user_id) {
        // 更新规则
        await handleUpdateUserId(rule, result.user_id);
        toast.success(`找到用户ID: ${result.user_id}`);
      } else {
        toast.error(`未找到用户: ${rule.sender_email}`);
      }
    } catch (error) {
      console.error('查询用户失败:', error);
      toast.error('查询用户失败');
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchRules();
  }, [searchTerm, filterType, filterActive, sortBy, sortOrder, currentPage]);

  const SortButton: React.FC<{ field: string; children: React.ReactNode }> = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center space-x-1 px-2 py-1 rounded text-sm ${
        sortBy === field 
          ? 'bg-blue-100 text-blue-700' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      <span>{children}</span>
      <ArrowUpDown className="w-3 h-3" />
      {sortBy === field && (
        <span className="text-xs">
          {sortOrder === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );

  return (
    <div className="h-full overflow-y-auto theme-bg-secondary">
      <div className="max-w-7xl mx-auto p-6">
        {/* 页面头部 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold theme-text-primary">用户管理</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button 
              onClick={handleBatchUpdateUserIds} 
              loading={batchUpdating} 
              variant="primary"
              disabled={batchUpdating}
            >
              {batchUpdating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              一键配置用户ID
            </Button>
            <Button onClick={fetchRules} loading={loading} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              刷新
            </Button>
          </div>
        </div>

        {/* 批量配置结果显示 */}
        {showBatchResults && batchResults && (
          <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border theme-border-primary">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium theme-text-primary">批量配置结果</h3>
              <button
                onClick={() => setShowBatchResults(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{batchResults.success}</div>
                <div className="text-sm text-green-600">成功配置</div>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{batchResults.failed}</div>
                <div className="text-sm text-red-600">配置失败</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{batchResults.results.length}</div>
                <div className="text-sm text-blue-600">总计处理</div>
              </div>
            </div>
            
            {batchResults.results.length > 0 && (
              <div className="max-h-64 overflow-y-auto">
                <div className="space-y-2">
                  {batchResults.results.map((result, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-2 rounded text-sm ${
                        result.success 
                          ? 'bg-green-50 text-green-700' 
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {result.success ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                        <span className="font-medium">{result.email}</span>
                      </div>
                      <div>
                        {result.success ? (
                          <span className="font-mono text-xs bg-green-100 px-2 py-1 rounded">
                            ID: {result.user_id}
                          </span>
                        ) : (
                          <span className="text-xs">{result.error}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 筛选和搜索工具栏 */}
        <div className="theme-bg-primary rounded-lg border theme-border-primary p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* 搜索框 */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 theme-text-tertiary w-4 h-4" />
                <input
                  type="text"
                  placeholder="搜索用户邮箱"
                  className="w-full pl-10 pr-4 py-2 theme-input theme-border-focus rounded-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* 筛选器 */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 theme-text-secondary" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="theme-input theme-border-focus rounded px-3 py-1 text-sm"
                >
                  <option value="all">所有类型</option>
                  <option value="user">用户</option>
                  <option value="non_user">非用户</option>
                </select>
              </div>

            </div>
          </div>
        </div>

        {/* 规则列表 */}
        <div className="theme-bg-primary rounded-lg border theme-border-primary overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-1/5" /> {/* 用户信息 - 缩小宽度 */}
                <col className="w-1/7" /> {/* 规则类型 */}
                <col className="w-1/5" /> {/* 标签 - 保持足够宽度用于编辑 */}
              </colgroup>
              <thead className="theme-bg-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium theme-text-secondary uppercase tracking-wider">
                    邮箱
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium theme-text-secondary uppercase tracking-wider">
                    用户ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium theme-text-secondary uppercase tracking-wider">
                    标签
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium theme-text-secondary uppercase tracking-wider">
                    类型
                  </th>
                </tr>
              </thead>
              <tbody className="theme-bg-primary divide-y theme-border-primary">
                {rules.map((rule) => (
                  <SenderRuleRow
                    key={rule.id}
                    rule={rule}
                    onToggle={() => handleToggleRule(rule)}
                    onEdit={() => setEditingRule(rule)}
                    onDelete={() => handleDeleteRule(rule)}
                    onUpdateTags={handleUpdateTags}
                    onUpdateUserId={handleUpdateUserId}
                    onUpdateRuleType={handleUpdateRuleType}
                    onNavigateToChat={handleNavigateToChat}
                    onLookupUser={handleLookupSingleUser}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    上一页
                  </Button>
                  <span className="text-sm text-gray-700">
                    第 {currentPage} 页，共 {totalPages} 页
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    下一页
                  </Button>
                </div>
                <div className="text-sm text-gray-500">
                  共 {total} 条记录
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 创建/编辑模态框 */}
        {(showCreateModal || editingRule) && (
          <SenderRuleModal
            rule={editingRule}
            onClose={() => {
              setShowCreateModal(false);
              setEditingRule(null);
            }}
            onSave={() => {
              fetchRules();
              setShowCreateModal(false);
              setEditingRule(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface SenderRuleRowProps {
  rule: SenderRule;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpdateTags: (rule: SenderRule, tags: string) => void;
  onUpdateUserId: (rule: SenderRule, user_id: string) => void;
  onUpdateRuleType: (rule: SenderRule, rule_type: string) => void;
  onNavigateToChat: (email: string) => void;
  onLookupUser: (rule: SenderRule) => void;
}

const SenderRuleRow: React.FC<SenderRuleRowProps> = ({ rule, onToggle, onEdit, onDelete, onUpdateTags, onUpdateUserId, onUpdateRuleType, onNavigateToChat, onLookupUser }) => {
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [editTags, setEditTags] = useState(rule.tags || '');

  const handleSaveTags = () => {
    onUpdateTags(rule, editTags);
    setIsEditingTags(false);
  };

  const handleCancelEditTags = () => {
    setEditTags(rule.tags || '');
    setIsEditingTags(false);
  };

  const [isEditingUserId, setIsEditingUserId] = useState(false);
  const [editUserId, setEditUserId] = useState(rule.user_id || '');

  const handleSaveUserId = () => {
    onUpdateUserId(rule, editUserId);
    setIsEditingUserId(false);
  };

  const handleCancelEditUserId = () => {
    setEditUserId(rule.user_id || '');
    setIsEditingUserId(false);
  };

  const [isEditingRuleType, setIsEditingRuleType] = useState(false);

  const handleRuleTypeChange = (newRuleType: string) => {
    onUpdateRuleType(rule, newRuleType);
    setIsEditingRuleType(false);
  };

  return (
    <tr className="theme-bg-hover">
      <td className="px-4 py-4">
        <div className="flex items-center">
          <div className="min-w-100 flex-1">
            <button
              onClick={() => onNavigateToChat(rule.sender_email)}
              className="text-sm font-medium theme-blue-text hover:text-blue-800 truncate cursor-pointer group flex items-center w-full text-left"
              title="查看反馈"
            >
              <span className="truncate">{rule.sender_email}</span>
              <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </button>
            {rule.sender_name && (
              <button
                onClick={() => onNavigateToChat(rule.sender_email)}
                className="text-xs theme-text-secondary hover:text-gray-700 truncate cursor-pointer w-full text-left"
                title="查看反馈"
              >
                {rule.sender_name}
              </button>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-4">
        {isEditingUserId ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={editUserId}
              onChange={(e) => setEditUserId(e.target.value)}
              placeholder="用户ID"
              className="flex-1 px-2 py-1 text-sm theme-input theme-border-focus rounded"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveUserId();
                } else if (e.key === 'Escape') {
                  handleCancelEditUserId();
                }
              }}
              autoFocus
            />
            <button
              onClick={handleSaveUserId}
              className="theme-green-text hover:text-green-800"
              title="保存"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancelEditUserId}
              className="theme-text-secondary hover:text-gray-800"
              title="取消"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <div 
              className="flex flex-wrap gap-1 cursor-pointer group"
              onClick={() => setIsEditingUserId(true)}
              title="点击编辑用户ID"
            >
              {rule.user_id ? (
                <span
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium theme-blue-bg theme-blue-text"
                >
                  <UserIcon className="w-3 h-3 mr-1" />
                  {rule.user_id}
                </span>
              ) : (
                <span className="theme-text-tertiary text-sm group-hover:text-gray-600">
                  点击配置用户ID
                </span>
              )}
              <Edit className="w-3 h-3 theme-text-tertiary opacity-0 group-hover:opacity-100 ml-1" />
            </div>
            {!rule.user_id && (
              <button
                onClick={() => onLookupUser(rule)}
                className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                title="从统计数据库查询用户ID"
              >
                <Zap className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-4">
        {isEditingTags ? (
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={editTags}
              onChange={(e) => setEditTags(e.target.value)}
              placeholder="用逗号分隔多个标签"
              className="flex-1 px-2 py-1 text-sm theme-input theme-border-focus rounded"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveTags();
                } else if (e.key === 'Escape') {
                  handleCancelEditTags();
                }
              }}
              autoFocus
            />
            <button
              onClick={handleSaveTags}
              className="theme-green-text hover:text-green-800"
              title="保存"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancelEditTags}
              className="theme-text-secondary hover:text-gray-800"
              title="取消"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div 
            className="flex flex-wrap gap-1 cursor-pointer group"
            onClick={() => setIsEditingTags(true)}
            title="点击编辑标签"
          >
            {rule.tags ? rule.tags.split(',').map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium theme-blue-bg theme-blue-text"
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag.trim()}
              </span>
            )) : (
              <span className="theme-text-tertiary text-sm group-hover:text-gray-600">
                点击添加标签
              </span>
            )}
            <Edit className="w-3 h-3 theme-text-tertiary opacity-0 group-hover:opacity-100 ml-1" />
          </div>
        )}
      </td>
      <td className="px-4 py-4">
        {isEditingRuleType ? (
          <div className="relative">
            <select
              value={rule.rule_type}
              onChange={(e) => handleRuleTypeChange(e.target.value)}
              className="w-full px-2 py-1 text-sm theme-input theme-border-focus rounded"
              autoFocus
              onBlur={() => setIsEditingRuleType(false)}
            >
              <option value="user">用户</option>
              <option value="non_user">非用户</option>
            </select>
          </div>
        ) : (
          <div 
            className="cursor-pointer group"
            onClick={() => setIsEditingRuleType(true)}
            title="点击切换规则类型"
          >
            <Badge variant={rule.rule_type === 'user' ? 'success' : 'warning'}>
              {getRuleTypeText(rule.rule_type)}
            </Badge>
            <Edit className="w-3 h-3 theme-text-tertiary opacity-0 group-hover:opacity-100 ml-1 inline" />
          </div>
        )}
      </td>
    </tr>
  );
};

interface SenderRuleModalProps {
  rule: SenderRule | null;
  onClose: () => void;
  onSave: () => void;
}

const SenderRuleModal: React.FC<SenderRuleModalProps> = ({ rule, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    sender_email: rule?.sender_email || '',
    sender_name: rule?.sender_name || '',
    rule_type: rule?.rule_type || 'user',
    is_user_email: rule?.is_user_email ?? true,
    auto_mark_read: rule?.auto_mark_read || false,
    hide_from_list: rule?.hide_from_list || false,
    tags: rule?.tags || '',
    priority: rule?.priority || 1,
    description: rule?.description || '',
    is_active: rule?.is_active ?? true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = rule 
        ? `${getApiBaseUrl()}/sender-rules/${rule.id}`
        : `${getApiBaseUrl()}/sender-rules`;
      
      const response = await fetch(url, {
        method: rule ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        onSave();
      } else {
        const error = await response.json();
        alert(error.detail || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      alert('操作失败');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">
            {rule ? '编辑用户规则' : '新建用户规则'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              用户邮箱 *
            </label>
            <input
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.sender_email}
              onChange={(e) => setFormData({...formData, sender_email: e.target.value})}
              disabled={!!rule}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              用户名称
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.sender_name}
              onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              类型 *
            </label>
            <select
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.rule_type}
              onChange={(e) => setFormData({...formData, rule_type: e.target.value})}
            >
              <option value="user">用户</option>
              <option value="non_user">非用户</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              标签
            </label>
            <input
              type="text"
              placeholder="多个标签用逗号分隔"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.tags}
              onChange={(e) => setFormData({...formData, tags: e.target.value})}
            />
            <p className="text-xs text-gray-500 mt-1">例如：重要客户,VIP,技术支持</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              优先级
            </label>
            <input
              type="number"
              min="1"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.priority}
              onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              描述
            </label>
            <textarea
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={formData.is_user_email}
                onChange={(e) => setFormData({...formData, is_user_email: e.target.checked})}
              />
              <span className="ml-2 text-sm text-gray-700">标记为用户邮件</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={formData.auto_mark_read}
                onChange={(e) => setFormData({...formData, auto_mark_read: e.target.checked})}
              />
              <span className="ml-2 text-sm text-gray-700">自动标记为已读</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={formData.hide_from_list}
                onChange={(e) => setFormData({...formData, hide_from_list: e.target.checked})}
              />
              <span className="ml-2 text-sm text-gray-700">在列表中隐藏</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              />
              <span className="ml-2 text-sm text-gray-700">激活规则</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              取消
            </Button>
            <Button type="submit">
              {rule ? '更新' : '创建'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SenderRulesPage; 